import React, { useState } from 'react';
import { Alert, Input, Switch } from 'antd';
// 결제 관리 화면과 같은 확인 모달 모양 · 간격을 쓴다
import { SettingsModal } from '../../pgProviderRoute/common/SettingsModal';
import { SettingChangesStyle } from '../../pgProviderRoute/common/styles';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';
import { FeatureFlagItem, FeatureFlagUpdateResponse } from '../../../lib/api/featureFlag';
import { useUpdateFeatureFlagMutation } from '../hooks/useFeatureFlagMutations';
import { STATUS_LABEL, nextOverride, onOff, splitDescription } from '../utils/featureFlagLabels';
import { SwitchModalStyle } from './styles';

export const FLAG_TEXT_MAX_LENGTH = 255;

type FeatureFlagSwitchModalProps = {
  flag: FeatureFlagItem;
  isProduction: boolean;
  onSaved: (response: FeatureFlagUpdateResponse) => void;
  onClose: () => void;
};

/**
 * 켜기 · 끄기 스위치 하나. 기본값과 같은 쪽이면 서버가 덮어쓴 값을 지우고, 다른 쪽이면 덮어쓴다.
 * 「기본값과 같은 값으로 덮어쓰기」는 지금은 효과가 없고 상태값을 되돌리는 PR 을 막는 함정이라 만들 수 없게 했다.
 */
export const FeatureFlagSwitchModal = (props: FeatureFlagSwitchModalProps): React.JSX.Element => {
  const { flag, isProduction, onSaved, onClose } = props;
  // 바꾸려고 연 모달이다 — 지금의 반대쪽에서 시작한다
  const [target, setTarget] = useState(!flag.enabled);
  const [reason, setReason] = useState('');
  const [memo, setMemo] = useState(flag.memo ?? '');
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateFeatureFlagMutation();

  const override = nextOverride(flag, target);
  const isChanged = target !== flag.enabled;
  const canSave = isChanged && reason.trim().length > 0 && !mutation.isLoading;
  const { text: description } = splitDescription(flag.description);
  const title = flag.ticketNumber ?? flag.ticketKey;

  const targetHint = !isChanged
    ? '지금과 같습니다'
    : override === null
      ? '기본값과 같습니다 — 덮어쓴 값을 지웁니다'
      : `기본값 ${onOff(flag.defaultEnabled)} 을 덮어씁니다`;

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setError(null);
    try {
      const response = await mutation.mutateAsync({
        ticketKey: flag.ticketKey,
        body: {
          enabled: target,
          reason: reason.trim(),
          // 메모는 덮어쓴 값에 붙는다 — 기본값으로 돌아가면 서버가 행과 함께 지운다
          ...(override !== null ? { memo: memo.trim() || null } : {}),
        },
      });
      onSaved(response);
    } catch (saveError) {
      setError(
        saveError instanceof Error ? extractErrorMessage(saveError) : '저장하지 못했습니다. 다시 시도해 주세요.',
      );
    }
  };

  return (
    <SettingsModal
      open
      destroyOnClose
      title={`${title} 값 바꾸기`}
      width={680}
      okText={isProduction ? 'prod 에 저장' : '저장'}
      cancelText="취소"
      onOk={handleSave}
      onCancel={onClose}
      confirmLoading={mutation.isLoading}
      okButtonProps={{ disabled: !canSave, danger: isProduction }}
      cancelButtonProps={{ disabled: mutation.isLoading }}
      closable={!mutation.isLoading}
      maskClosable={!mutation.isLoading}
      keyboard={!mutation.isLoading}
    >
      <SwitchModalStyle>
        {isProduction ? (
          <Alert
            type="error"
            showIcon
            message="운영(prod) 환경입니다."
            description="저장하면 재시작 없이 몇 초 안에 운영 서버 전체에 반영됩니다. 이미 돌고 있는 cron 실행 · 큐에 들어간 잡은 끝까지 옛 값으로 돕니다."
          />
        ) : (
          <Alert type="info" showIcon message="저장하면 재시작 없이 몇 초 안에 이 환경의 서버 전체에 반영됩니다." />
        )}
        {error && <Alert type="error" showIcon message={error} />}

        <dl className="flag-summary">
          <dt>플래그</dt>
          <dd>{description || '설명 없음'}</dd>
          <dt>상태값</dt>
          <dd>
            {flag.status ? STATUS_LABEL[flag.status] : '-'} · 기본값 {onOff(flag.defaultEnabled)}
          </dd>
        </dl>

        <div>
          <span className="flag-field-label">이 환경에서</span>
          <div className="flag-switch-row">
            <div>
              <strong>{onOff(target)}</strong>
              <span className="settings-muted">{targetHint}</span>
            </div>
            <Switch
              aria-label={`${title} 켜기`}
              checked={target}
              checkedChildren="ON"
              unCheckedChildren="OFF"
              disabled={mutation.isLoading}
              onChange={setTarget}
            />
          </div>
          <div className="flag-field-hint">
            기본값과 같은 쪽을 고르면 덮어쓴 값을 지우고 상태값을 따라갑니다. 다른 쪽을 고르면 상태값이 바뀌어도(PR
            배포) 이 환경은 그 값으로 남습니다.
          </div>
        </div>

        <div>
          <span className="flag-field-label">지금 판정</span>
          <SettingChangesStyle>
            <div className="change-values">
              <div>
                <small>변경 전</small>
                {onOff(flag.enabled)} — {flag.dbEnabled === null ? '기본값 따름' : '덮어씀'}
              </div>
              <span className="change-arrow">→</span>
              <div>
                <small>변경 후</small>
                {onOff(target)} — {override === null ? '기본값 따름' : '덮어씀'}
                {!isChanged && ' (바뀌지 않음)'}
              </div>
            </div>
          </SettingChangesStyle>
        </div>

        <div>
          <label className="flag-field-label" htmlFor="feature-flag-reason">
            변경 사유 (필수)
          </label>
          <Input.TextArea
            id="feature-flag-reason"
            rows={3}
            maxLength={FLAG_TEXT_MAX_LENGTH}
            showCount
            placeholder="예) 추천 결과 이상 — blux 적재로 되돌림 (원인 확인 후 다시 ON)"
            value={reason}
            disabled={mutation.isLoading}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flag-field-hint">사유는 이력에 남습니다. 비우면 저장할 수 없습니다.</div>
        </div>

        {override !== null && (
          <div>
            <label className="flag-field-label" htmlFor="feature-flag-memo">
              메모 (선택)
            </label>
            <Input
              id="feature-flag-memo"
              maxLength={FLAG_TEXT_MAX_LENGTH}
              placeholder="예) 원인 확인 전까지 OFF 유지"
              value={memo}
              disabled={mutation.isLoading}
              onChange={(event) => setMemo(event.target.value)}
            />
            <div className="flag-field-hint">목록에 계속 보입니다. 기본값으로 돌아가면 함께 지워집니다.</div>
          </div>
        )}
      </SwitchModalStyle>
    </SettingsModal>
  );
};
